import { FC } from "react";

import { EventBusTopic } from "@moodys/mdc-frontend.utils.event-bus";
import { MenuItem, Typography, useTheme } from "@mui/material";

import { split } from "@moodys/federated";
import { useMenu } from "components/Menu/context";
import { useEventBus } from "hooks/useEventBus";
import { SPLIT_PROFILE_PAGE } from "services/split/split.constants";
import { generateHeaderAnalyticsProp } from "utils/generateAnalyticsTag";
import { getLanguageFromCookie, getPageLanguage } from "utils/language";

import { accountMenuStyles } from "../../AccountMenu.styles";
import { useAccountMenuTranslation } from "../../i18n";
import { MenuLink } from "../../model";
import { NewMenuItemChip } from "../NewMenuItemChip/NewMenuItemChip";

interface AccountMenuItemProps {
  node: MenuLink.Link;
  onClick?: VoidFunction;
}

const extractHref = (showNewProfilePage: boolean, node: MenuLink.Link) => {
  return showNewProfilePage && node.titleKey === "profile"
    ? "/account/profile"
    : node.href[getLanguageFromCookie()] || node.href.en;
};

export const AccountMenuLink: FC<AccountMenuItemProps> = ({
  node,
  onClick,
}) => {
  const theme = useTheme();
  const { languageNodes } = useMenu();
  const [userDetails] = useEventBus(EventBusTopic.UserDetails);
  const { translate } = useAccountMenuTranslation(
    getPageLanguage(languageNodes)
  );
  const accountMenuButtonLabel = translate("openAccountMenu");

  const name = translate(node.titleKey);
  const { treatments } = split.useSplitTreatments({
    names: [SPLIT_PROFILE_PAGE],
    attributes: { email: userDetails?.email || "" },
  });

  const showNewProfilePage = treatments[SPLIT_PROFILE_PAGE].treatment === "on";

  const menuItemProps = onClick
    ? { onClick, component: "button", sx: { width: "100%" } }
    : {
        href: extractHref(showNewProfilePage, node),
        component: "a",
      };
  return (
    <MenuItem
      {...menuItemProps}
      {...generateHeaderAnalyticsProp({
        moduleName: accountMenuButtonLabel,
        name,
      })}
      data-testid={`account-menu-link-${node.titleKey}`}
    >
      <Typography
        component="span"
        css={[
          accountMenuStyles.linkText(theme),
          !!node.highlighted && accountMenuStyles.highlightedText(theme),
        ]}
      >
        {name}
        {node.hasNewLabel && <NewMenuItemChip />}
      </Typography>
    </MenuItem>
  );
};
